import React, { useEffect, useState } from 'react';
import { Button } from '@app/components/common/buttons/Button/Button';
import { UserListModel, UserModel } from '@app/models/UserModel';
import { ColumnsType } from 'antd/es/table';
import { Table } from '@app/components/common/Table/Table';
import { PageTitle, Toolbar, ToolbarActions } from '@app/components/common/PageToolbar/PageToolbar';
import { useAppDispatch, useAppSelector } from '@app/hooks/reduxHooks';
import { doGetFeatureRequests, setFeatureRequests } from '@app/store/slices/dataSlice';
import { notificationController } from '@app/controllers/notificationController';
import {
  Avatar, Badge, Card, Checkbox, Divider, Drawer, Dropdown, Empty, Input, Modal, Popconfirm, Space, Spin,
  Tag, Typography,
} from 'antd';
import { DayjsDatePicker } from '@app/components/common/pickers/DayjsDatePicker';
import dayjs from 'dayjs';
import {
  CheckCircleFilled, CommentOutlined, DeleteOutlined, FilterOutlined, LikeFilled, LikeOutlined, PlusOutlined,
} from '@ant-design/icons';
import { Card as CommonCard } from '@app/components/common/Card/Card';
import { getModeDisplay, getProfilePhoto, secondsToString } from '@app/utils/utils';
import { useNavigate } from 'react-router-dom';
import {
  createFeatureRequest,
  deleteFeatureComment,
  deleteFeatureRequest,
  getFeatureComments,
  likeFeatureComment,
  postFeatureComment,
  unlikeFeatureComment,
  updateFeatureRequest,
} from '@app/api/admin.api';
import { Switch } from '@app/components/common/Switch/Switch';
import { FeatureComment, FeatureRequest, brandLabel, brandList } from './types';

// Same shape as the Users and Shopify Orders pages: draft vs applied, a
// Filters button with a count badge, and a dropdown panel. Filtering is
// client-side here because the page already loads the full list.
type RequestFilters = {
  brand: string[];
  author: string[];
  pinned: string[];
  comments: string[];
  from: string | null;
  to: string | null;
};

const EMPTY_FILTERS: RequestFilters = {
  brand: [], author: [], pinned: [], comments: [], from: null, to: null,
};

const countActive = (f: RequestFilters): number =>
  (f.brand.length ? 1 : 0) + (f.author.length ? 1 : 0) + (f.pinned.length ? 1 : 0) +
  (f.comments.length ? 1 : 0) + (f.from || f.to ? 1 : 0);

const matchesFilters = (r: FeatureRequest, f: RequestFilters): boolean => {
  if (f.brand.length && !f.brand.some((b) => brandList(r.brand).includes(b))) return false;
  if (f.author.length) {
    const kind = r.is_team ? 'team' : 'user';
    if (!f.author.includes(kind)) return false;
  }
  if (f.pinned.length) {
    const kind = r.is_pinned ? 'pinned' : 'unpinned';
    if (!f.pinned.includes(kind)) return false;
  }
  if (f.comments.length) {
    const kind = r.total_comments > 0 ? 'with' : 'without';
    if (!f.comments.includes(kind)) return false;
  }
  if (f.from && dayjs(r.created_at).isBefore(dayjs(f.from), 'day')) return false;
  if (f.to && dayjs(r.created_at).isAfter(dayjs(f.to), 'day')) return false;
  return true;
};

const { RangePicker } = DayjsDatePicker;

/** Upvotes minus downvotes -- what "most voted" should actually mean. */
const netScore = (r: FeatureRequest): number =>
  (r.total_upvote || 0) - (r.total_downvote || 0);

const FeatureRequestsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const featureRequests = useAppSelector((state) => state.data.featureRequests);

  const [isLoading, setLoading] = useState(false);
  const [deleteOpenModal, setDeleteOpenModal] = useState(0);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [keyWord, setKeyWord] = useState('')
  const [filteredFeatureRequests, setFilteredFeatureRequests] = useState([])

  const [applied, setApplied] = useState<RequestFilters>(EMPTY_FILTERS);
  const [draft, setDraft] = useState<RequestFilters>(EMPTY_FILTERS);
  const [filterOpen, setFilterOpen] = useState(false);

  // Compose
  const [composeOpen, setComposeOpen] = useState(false);
  const [newDescription, setNewDescription] = useState('');
  const [newBrands, setNewBrands] = useState<string[]>(['ease']);
  const [posting, setPosting] = useState(false);

  // Comments
  const [commentsFor, setCommentsFor] = useState<FeatureRequest | null>(null);
  const [comments, setComments] = useState<FeatureComment[]>([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [reply, setReply] = useState('');
  const [replying, setReplying] = useState(false);

  const fetchFeatureRequests = () => {
    setLoading(true);
    dispatch(doGetFeatureRequests())
      .unwrap()
      .then((res) => {
        dispatch(setFeatureRequests(res));
        setLoading(false);
      })
      .catch((err) => {
        notificationController.error({ message: err.message });
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchFeatureRequests();
  }, []);

  useEffect(() => {
    const term = keyWord.trim().toLowerCase();
    setFilteredFeatureRequests(
      featureRequests
        .filter((data: FeatureRequest) => matchesFilters(data, applied))
        .filter((data: FeatureRequest) =>
          !term
          || data.created_by?.name?.toLowerCase().includes(term)
          || data.description?.toLowerCase().includes(term)
          || data.brand?.toLowerCase().includes(term)),
    );
  }, [keyWord, featureRequests, applied])

  const onPin = (isPinned: boolean, recordId: number) => {
    const updatedFeature = { is_pinned: isPinned }
    updateFeatureRequest(recordId, updatedFeature)
      .then(res => {
        console.log('updatedFeature', res);
        const updatedFeatures = featureRequests.map(data => (data.id === res.id ? res : data));
        dispatch(setFeatureRequests(updatedFeatures));
        notificationController.success({ message: 'Post updated successfully' })
      })
      .catch(error => {
        notificationController.error({ message: error.message });
      })
  }

  const onDelete = (record: FeatureRequest) => {
    setDeleteLoading(true);

    deleteFeatureRequest(record.id)
      .then(_ => {
        setDeleteLoading(false);
        setDeleteOpenModal(null);
        fetchFeatureRequests();
        notificationController.success({ message: 'Post deleted successfully' })
      })
      .catch(error => {
        setDeleteLoading(false);
        setDeleteOpenModal(null);
        notificationController.error({ message: error.message });
      })
  }

  const onPost = () => {
    if (!newDescription.trim()) {
      notificationController.error({ message: 'Please enter a description.' });
      return;
    }
    if (!newBrands.length) {
      notificationController.error({ message: 'Pick at least one brand.' });
      return;
    }
    setPosting(true);
    // One row carrying both brands: the backend list filter is `icontains`, so
    // "ease,wavon" is returned to both feeds without any app change.
    createFeatureRequest({ description: newDescription.trim(), brand: newBrands.join(',') })
      .then(() => {
        setComposeOpen(false);
        setNewDescription('');
        setNewBrands(['ease']);
        fetchFeatureRequests();
        notificationController.success({ message: 'Feature request posted' });
      })
      .catch((error) => notificationController.error({ message: error.message }))
      .finally(() => setPosting(false));
  };

  const openComments = (record: FeatureRequest) => {
    setCommentsFor(record);
    setComments([]);
    setCommentsLoading(true);
    getFeatureComments(record.id)
      .then(setComments)
      .catch((error) => notificationController.error({ message: error.message }))
      .finally(() => setCommentsLoading(false));
  };

  const onReply = () => {
    if (!commentsFor || !reply.trim()) return;
    setReplying(true);
    postFeatureComment(commentsFor.id, reply.trim())
      .then((created) => {
        setComments((prev) => [...prev, created]);
        setReply('');
        // Keep the row's comment count honest without a full refetch.
        fetchFeatureRequests();
      })
      .catch((error) => notificationController.error({ message: error.message }))
      .finally(() => setReplying(false));
  };

  const onDeleteComment = (comment: FeatureComment) => {
    if (!commentsFor) return;
    deleteFeatureComment(commentsFor.id, comment.id)
      .then(() => {
        setComments((prev) => prev.filter((c) => c.id !== comment.id));
        fetchFeatureRequests();
        notificationController.success({ message: 'Comment removed' });
      })
      .catch((error) => notificationController.error({ message: error.message }));
  };

  const onToggleLike = (comment: FeatureComment) => {
    const action = comment.user_has_liked ? unlikeFeatureComment : likeFeatureComment;
    action(comment.id)
      .then((updated) => setComments((prev) => prev.map((c) => (c.id === updated.id ? updated : c))))
      .catch((error) => notificationController.error({ message: error.message }));
  };

  const setD = (patch: Partial<RequestFilters>) => setDraft((d) => ({ ...d, ...patch }));

  const checkboxGroup = (
    label: string,
    values: string[],
    opts: { label: string; value: string }[],
    onChange: (v: string[]) => void,
    minWidth = 150,
  ) => (
    <div style={{ minWidth }}>
      <div style={{ fontWeight: 600, marginBottom: 6 }}>{label}</div>
      <Checkbox.Group
        value={values}
        options={opts}
        onChange={(v) => onChange(v as string[])}
        style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 180, overflowY: 'auto' }}
      />
    </div>
  );

  // Same shell as the Users and Shopify Orders panels: an AntD Card with a
  // divider above the footer, and a click guard so ticking a box does not
  // close the dropdown.
  const filterPanel = (
    <div onClick={(e) => e.stopPropagation()}>
      <Card
        size="small"
        style={{ width: 640, maxWidth: '92vw', boxShadow: '0 6px 24px rgba(0,0,0,0.18)' }}
        bodyStyle={{ maxHeight: '70vh', overflowY: 'auto' }}
      >
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px 28px' }}>
          {checkboxGroup('Brand', draft.brand, [
            { label: 'Ease', value: 'ease' },
            { label: 'Wavon', value: 'wavon' },
          ], (v) => setD({ brand: v }), 110)}
          {checkboxGroup('Posted by', draft.author, [
            { label: 'Support team', value: 'team' },
            { label: 'App users', value: 'user' },
          ], (v) => setD({ author: v }), 150)}
          {checkboxGroup('Pinned', draft.pinned, [
            { label: 'Pinned', value: 'pinned' },
            { label: 'Not pinned', value: 'unpinned' },
          ], (v) => setD({ pinned: v }), 130)}
          {checkboxGroup('Comments', draft.comments, [
            { label: 'Has comments', value: 'with' },
            { label: 'No comments', value: 'without' },
          ], (v) => setD({ comments: v }), 150)}
          <div style={{ minWidth: 220 }}>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>Posted between</div>
            <RangePicker
              style={{ width: '100%' }}
              value={[draft.from ? dayjs(draft.from) : null, draft.to ? dayjs(draft.to) : null] as never}
              onChange={(d: any) =>
                setD({
                  from: d?.[0] ? d[0].format('YYYY-MM-DD') : null,
                  to: d?.[1] ? d[1].format('YYYY-MM-DD') : null,
                })
              }
            />
          </div>
        </div>

        <Divider style={{ margin: '14px 0' }} />
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Space>
            <Button type="ghost" onClick={() => setDraft(EMPTY_FILTERS)}>Clear all</Button>
          </Space>
          <Button
            type="primary"
            onClick={() => {
              setApplied(draft);
              setFilterOpen(false);
            }}
          >
            Apply
          </Button>
        </div>
      </Card>
    </div>
  );

  const columns: ColumnsType<FeatureRequest> = [
    {
      title: 'Created By',
      dataIndex: 'created_by',
      width: '15%',
      render: (_, record: FeatureRequest) => (
        <Space size={'small'}>
          <Avatar src={getProfilePhoto(record.created_by.profile_picture)} alt="User" shape="circle" size={40} />
          <Typography.Text>{record.created_by.name}</Typography.Text>
          {record.is_team && (
            <CheckCircleFilled style={{ color: '#1D9BF0' }} title="Posted by the support team" />
          )}
        </Space>
      ),
    },
    {
      title: 'Description',
      dataIndex: ['description'],
      ellipsis: true,
      // Full text is one click away in the comments drawer, so truncate rather
      // than let a long request make every row three lines tall.
      render: (text: string) => <span title={text}>{text}</span>,
    },
    {
      title: 'Votes',
      dataIndex: 'total_upvote',
      width: 130,
      align: 'right',
      // Net score, so a heavily downvoted request does not outrank a genuinely
      // popular one just by having more total activity.
      sorter: (a: FeatureRequest, b: FeatureRequest) => netScore(a) - netScore(b),
      // Descending first: the point of sorting here is "what do people want
      // most", so one click should answer it.
      sortDirections: ['descend', 'ascend'] as never,
      render: (_: number, request: FeatureRequest) => (
        <Space direction="vertical" size={0} style={{ alignItems: 'flex-end' }}>
          <Typography.Text strong>{netScore(request) > 0 ? '+' : ''}{netScore(request)}</Typography.Text>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {request.total_upvote || 0} up · {request.total_downvote || 0} down
          </Typography.Text>
        </Space>
      ),
    },
    {
      title: 'Comments',
      dataIndex: 'total_comments',
      width: 110,
      align: 'right',
      sorter: (a: FeatureRequest, b: FeatureRequest) =>
        (a.total_comments || 0) - (b.total_comments || 0),
      sortDirections: ['descend', 'ascend'] as never,
      render: (value: number) => value || 0,
    },
    {
      title: 'Posted',
      dataIndex: 'created_at',
      width: 120,
      sorter: (a: FeatureRequest, b: FeatureRequest) =>
        dayjs(a.created_at).valueOf() - dayjs(b.created_at).valueOf(),
      sortDirections: ['descend', 'ascend'] as never,
      render: (value: string) => (value ? dayjs(value).format('MMM D, YYYY') : '—'),
    },
    {
      title: 'Brand',
      dataIndex: ['brand'],
      width: 110,
      render: (text: string) => <Tag>{brandLabel(text)}</Tag>,
      // Filtering lives in the shared Filters dropdown now, for consistency
      // with the Users and Shopify Orders pages.
    },
    {
      title: 'Pin',
      dataIndex: 'pin',
      width: 120,
      render: (text: string, request: FeatureRequest) => {
        return (
          <Space>
            <Switch
              checkedChildren="Pinned"
              unCheckedChildren="Pin"
              checked={request.is_pinned}
              onChange={(checked) => onPin(checked, request.id)}
              style={{ backgroundColor: request.is_pinned && 'green' }}
            />
          </Space>
        );
      },
    },
    {
      title: 'Actions',
      dataIndex: 'actions',
      width: 110,
      render: (text: string, record: FeatureRequest) => {
        return (
          <Space>
            <Button
              size="small"
              icon={<CommentOutlined />}
              onClick={() => openComments(record)}
              title="View and reply to comments"
            />
            <Popconfirm
              visible={deleteOpenModal === record.id}
              title="Are you sure you want to delete this post?"
              okButtonProps={{ loading: deleteLoading }}
              onConfirm={() => onDelete(record)}
              onCancel={() => setDeleteOpenModal(null)}>
              <Button size="small" type="primary" icon={<DeleteOutlined />} onClick={() => !deleteLoading && setDeleteOpenModal(record.id)} />
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  return (
    <>
      <Toolbar>
        <PageTitle>Feature Requests</PageTitle>
        <ToolbarActions>
          <Input
            style={{ width: 340 }}
            placeholder={'Search by keyword'}
            value={keyWord}
            onChange={(e) => setKeyWord(e.target.value)}
            allowClear
          />
          <Dropdown
            visible={filterOpen}
            onVisibleChange={(open) => {
              if (open) setDraft(applied);
              setFilterOpen(open);
            }}
            trigger={['click']}
            placement="bottomRight"
            overlay={filterPanel}
          >
            <Badge count={countActive(applied)} size="small">
              <Button icon={<FilterOutlined />}>Filters</Button>
            </Badge>
          </Dropdown>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setComposeOpen(true)}>
            New request
          </Button>
        </ToolbarActions>
      </Toolbar>
      <CommonCard id="basic-table" padding="1.25rem 1.25rem 0">
        <Table
          columns={columns}
          dataSource={filteredFeatureRequests}
          loading={isLoading}
          rowKey="id"
          onRow={(record: FeatureRequest) => ({
            style: { cursor: 'pointer' },
            onClick: (event) => {
              // The Pin switch and the action buttons live inside the row;
              // without this a click on either would also open the drawer.
              if ((event.target as HTMLElement).closest('button, .ant-switch, .ant-popover')) return;
              openComments(record);
            },
          })}
        />
      </CommonCard>

      <Modal
        title="New feature request"
        visible={composeOpen}
        onCancel={() => setComposeOpen(false)}
        onOk={onPost}
        okText="Post"
        confirmLoading={posting}
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <div>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>Description</div>
            <Input.TextArea
              rows={4}
              value={newDescription}
              onChange={(e) => setNewDescription(e.target.value)}
              placeholder="What should we build?"
            />
          </div>
          <div>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>Show to</div>
            <Checkbox.Group
              value={newBrands}
              onChange={(v) => setNewBrands(v as string[])}
              options={[
                { label: 'Ease', value: 'ease' },
                { label: 'Wavon', value: 'wavon' },
              ]}
            />
          </div>
          <Typography.Text type="secondary">
            Posted as the support team, and visible in the app feed of every brand ticked.
          </Typography.Text>
        </Space>
      </Modal>

      <Drawer
        title="Feature request"
        placement="right"
        width={560}
        visible={!!commentsFor}
        onClose={() => setCommentsFor(null)}
      >
        {/* The table truncates the description, so the drawer carries the full
            post. It is the only place the whole thing is readable. */}
        {commentsFor && (
          <div
            style={{
              padding: 14,
              marginBottom: 20,
              background: 'var(--soft-panel-bg)',
              border: 'var(--soft-panel-border)',
              borderRadius: 8,
            }}
          >
            <Space size="small" style={{ marginBottom: 8 }}>
              <Avatar src={getProfilePhoto(commentsFor.created_by?.profile_picture)} size={28} />
              <Typography.Text strong>{commentsFor.created_by?.name || 'Unknown'}</Typography.Text>
              {commentsFor.is_team && (
                <CheckCircleFilled style={{ color: '#1D9BF0', fontSize: 12 }} />
              )}
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {commentsFor.created_at ? dayjs(commentsFor.created_at).format('MMM D, YYYY') : ''}
              </Typography.Text>
            </Space>

            <Typography.Paragraph style={{ marginBottom: 10, whiteSpace: 'pre-wrap' }}>
              {commentsFor.description}
            </Typography.Paragraph>

            <Space size="small" wrap>
              <Tag>{brandLabel(commentsFor.brand)}</Tag>
              {commentsFor.is_pinned && <Tag color="green">Pinned</Tag>}
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {commentsFor.total_upvote || 0} up · {commentsFor.total_downvote || 0} down
              </Typography.Text>
            </Space>
          </div>
        )}

        <Typography.Text strong style={{ display: 'block', marginBottom: 10 }}>
          Comments{comments.length ? ` (${comments.length})` : ''}
        </Typography.Text>

        {commentsLoading ? (
          <div style={{ textAlign: 'center', padding: 24 }}>
            <Spin />
          </div>
        ) : comments.length ? (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            {comments.map((c) => (
              <div key={c.id} style={{ padding: 12, background: 'var(--soft-panel-bg)',
                                       border: 'var(--soft-panel-border)', borderRadius: 8 }}>
                <Space size="small" style={{ marginBottom: 6 }}>
                  <Avatar src={getProfilePhoto(c.created_by?.profile_picture)} size={26} />
                  <Typography.Text strong>{c.created_by?.name || 'Unknown'}</Typography.Text>
                  {c.is_team && (
                    <CheckCircleFilled
                      style={{ color: '#1D9BF0', fontSize: 12 }}
                      title="Support team"
                    />
                  )}
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {new Date(c.created_time).toLocaleString()}
                  </Typography.Text>
                </Space>
                <div>{c.comment}</div>
                <Space size="small" style={{ marginTop: 8 }}>
                  <Button
                    size="small"
                    type="text"
                    icon={c.user_has_liked ? <LikeFilled /> : <LikeOutlined />}
                    onClick={() => onToggleLike(c)}
                  >
                    {c.total_likes}
                  </Button>
                  <Popconfirm
                    title="Remove this comment?"
                    okText="Remove"
                    okButtonProps={{ danger: true }}
                    onConfirm={() => onDeleteComment(c)}
                  >
                    <Button size="small" type="text" danger icon={<DeleteOutlined />}>
                      Remove
                    </Button>
                  </Popconfirm>
                </Space>
              </div>
            ))}
          </Space>
        ) : (
          <Empty description="No comments yet" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        )}

        <div style={{ marginTop: 20 }}>
          <Input.TextArea
            rows={3}
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            placeholder="Reply as the support team…"
          />
          <Button
            type="primary"
            loading={replying}
            disabled={!reply.trim()}
            onClick={onReply}
            style={{ marginTop: 8 }}
          >
            Post reply
          </Button>
        </div>
      </Drawer>
    </>
  );
};

export default FeatureRequestsPage;
